// ============================================================
// Analytics helpers — tous les calculs côté client
// ============================================================

import { DAY_MS, WEEK_MS } from '../constants';

// ============================================================
// V7.5.1 — Détection des games annulées (remake / AFK early)
// ============================================================

/**
 * Une game est "annulée" si Riot a flag `gameEndedInEarlySurrender` à true
 * (= remake automatique après AFK détecté < 3min30, ratifié par le reste de l'équipe).
 * Ces games ne sont pas comptabilisées dans les stats Riot officielles
 * et ne donnent ni LP ni dégâts ni KDA significatifs.
 *
 * On utilise le flag officiel Riot plutôt qu'un seuil de durée pour ne pas
 * confondre avec un FF15 (qui dure 14-15 min mais reste une "vraie" game).
 */
/**
 * Détecte si une game est "annulée" (remake automatique Riot).
 * Utilise le flag officiel `gameEndedInEarlySurrender` (alias isRemake côté DB).
 * Safety net : games < 5 min même sans le flag.
 *
 * V7.5.1 : utilise le flag officiel Riot plutôt qu'un seuil de durée
 * pour ne pas confondre avec un FF15 (qui dure 14-15 min mais reste une vraie game).
 */
export function isGameCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  // v7.7.9 FIX CRITIQUE : le parser stocke `duration` en MINUTES
  // (riot.js : Math.round(gameDuration / 60)), pas en secondes.
  // L'ancien seuil < 300 marquait TOUTES les games normales (20-35 min)
  // comme "annulées" → 107 games disparaissaient du dashboard.
  // Une game annulée/remake = moins de 5 minutes.
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

/**
 * Filtre une liste de games pour ne garder que les valides (non annulées).
 * À utiliser dans tous les calculs d'analytics.
 */
export function filterValidGames(games) {
  if (!Array.isArray(games)) return [];
  return games.filter(g => !isGameCancelled(g));
}

// ============================================================
// V6.2 — Patterns de morts (classification contextuelle)
// Détecte la "cause" tactique de chaque mort
// ============================================================
export const DEATH_PATTERNS = [
  { id: 'lane_death', label: 'Mort en lane', desc: 'Bot lane avant 15min', color: '#fb7185' },
  { id: 'catch_mid', label: 'Catch mid', desc: 'Mid solo après 15min', color: '#f59e0b' },
  { id: 'catch_jungle', label: 'Catch jungle', desc: 'Jungle ennemie solo', color: '#a78bfa' },
  { id: 'tf_death', label: 'Mort en TF', desc: 'Mort dans une teamfight', color: '#22d3ee' },
  { id: 'dive_fail', label: 'Dive raté', desc: 'Mort à la tower ennemie', color: '#ec4899' },
  { id: 'roam_fail', label: 'Roam fail', desc: 'Hors de bot avant 10min', color: '#fb923c' },
  { id: 'late_pick', label: 'Late game pick', desc: 'Solo après 25min', color: '#dc2626' },
  { id: 'gank_victim', label: 'Gank victim', desc: 'Mort sur gank jungler', color: '#fbbf24' }
];

/**
 * V6.2 — Classifie une mort dans 0-N patterns
 * Une mort peut matcher plusieurs patterns (ex: mort en lane + gank victim)
 */
export function classifyDeathPatterns(death) {
  const patterns = [];
  const minute = +death.minute || 0;
  const pos = death.position || { x: 0, y: 0 };
  const context = (death.context || '').toLowerCase();

  // Détecter zone depuis context (le parser stocke "bot", "mid", etc.)
  const isBot = context.includes('bot');
  const isMid = context.includes('mid');
  const isTop = context.includes('top');
  const isJungle = context.includes('jungle');
  const isTower = context.includes('tower') || context.includes('tour');
  const isTF = context.includes('tf') || context.includes('teamfight');
  const isGank = context.includes('gank');
  const isSolo = context.includes('solo');

  // 1. Mort en lane : bot + < 15min
  if (isBot && minute < 15) patterns.push('lane_death');

  // 2. Catch mid : mid + solo + ≥ 15min
  if (isMid && isSolo && minute >= 15) patterns.push('catch_mid');

  // 3. Catch jungle : jungle + solo
  if (isJungle && isSolo) patterns.push('catch_jungle');

  // 4. TF death
  if (isTF) patterns.push('tf_death');

  // 5. Dive fail : mort à la tower
  if (isTower) patterns.push('dive_fail');

  // 6. Roam fail : hors de bot avant 10min
  if (!isBot && !isJungle && minute < 10 && !isTF) patterns.push('roam_fail');

  // 7. Late game pick : solo ≥ 25min
  if (isSolo && minute >= 25) patterns.push('late_pick');

  // 8. Gank victim
  if (isGank) patterns.push('gank_victim');

  return patterns;
}

export function getDeathsAnalytics(games) {
  const allDeaths = games.flatMap(g => (g.deathDetails || []).map(d => ({ ...d, gameId: g.id, result: g.result })));
  const total = allDeaths.length;

  // Distribution par type
  const byType = { investment: 0, position: 0, greed: 0 };
  allDeaths.forEach(d => { byType[d.type] = (byType[d.type] || 0) + 1; });

  // Distribution temporelle (buckets de 5 min)
  const buckets = [
    { label: '0-5', min: 0, max: 5, investment: 0, position: 0, greed: 0 },
    { label: '5-10', min: 5, max: 10, investment: 0, position: 0, greed: 0 },
    { label: '10-15', min: 10, max: 15, investment: 0, position: 0, greed: 0 },
    { label: '15-20', min: 15, max: 20, investment: 0, position: 0, greed: 0 },
    { label: '20-25', min: 20, max: 25, investment: 0, position: 0, greed: 0 },
    { label: '25-30', min: 25, max: 30, investment: 0, position: 0, greed: 0 },
    { label: '30-35', min: 30, max: 35, investment: 0, position: 0, greed: 0 },
    { label: '35+', min: 35, max: 999, investment: 0, position: 0, greed: 0 }
  ];
  allDeaths.forEach(d => {
    const min = +d.minute;
    const bucket = buckets.find(b => min >= b.min && min < b.max);
    if (bucket) bucket[d.type]++;
  });

  // Trend rolling par type sur 10 dernières games
  const rollingWindow = 10;
  const trends = [];
  for (let i = 0; i < games.length; i++) {
    const window = games.slice(Math.max(0, i - rollingWindow + 1), i + 1);
    const windowDeaths = window.flatMap(g => g.deathDetails || []);
    trends.push({
      gameNumber: i + 1,
      investment: windowDeaths.filter(d => d.type === 'investment').length / window.length,
      position: windowDeaths.filter(d => d.type === 'position').length / window.length,
      greed: windowDeaths.filter(d => d.type === 'greed').length / window.length,
      total: windowDeaths.length / window.length
    });
  }

  // Contextes les plus fréquents (mots-clés)
  const contexts = {};
  allDeaths.forEach(d => {
    if (!d.context) return;
    // Extract keywords (>2 chars, not common words)
    const stopWords = ['sur', 'dans', 'avec', 'pour', 'une', 'des', 'les', 'que', 'qui', 'pas', 'mais', 'trop', 'puis', 'cas', 'part', 'fait'];
    const words = d.context.toLowerCase().replace(/[.,;:!?()'"]/g, '').split(/\s+/).filter(w => w.length > 2 && !stopWords.includes(w));
    words.forEach(w => { contexts[w] = (contexts[w] || 0) + 1; });
  });
  const topContexts = Object.entries(contexts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([word, count]) => ({ word, count }));

  // Morts par résultat
  const deathsInWins = games.filter(g => g.result === 'W').reduce((a, g) => a + g.deaths, 0);
  const deathsInLosses = games.filter(g => g.result === 'L').reduce((a, g) => a + g.deaths, 0);
  const wins = games.filter(g => g.result === 'W').length;
  const losses = games.filter(g => g.result === 'L').length;

  // V6.2 : Distribution des patterns
  const patternCounts = {};
  DEATH_PATTERNS.forEach(p => { patternCounts[p.id] = 0; });
  let deathsWithPatterns = 0; // morts qui ont matché au moins 1 pattern
  allDeaths.forEach(d => {
    const matched = classifyDeathPatterns(d);
    if (matched.length > 0) deathsWithPatterns++;
    matched.forEach(pid => { patternCounts[pid]++; });
  });
  const patterns = DEATH_PATTERNS.map(p => ({
    ...p,
    count: patternCounts[p.id],
    pct: total > 0 ? Math.round(patternCounts[p.id] / total * 100) : 0
  })).sort((a, b) => b.count - a.count);

  return {
    total,
    byType,
    buckets,
    trends,
    topContexts,
    avgDeathsInWins: wins > 0 ? (deathsInWins / wins).toFixed(1) : 0,
    avgDeathsInLosses: losses > 0 ? (deathsInLosses / losses).toFixed(1) : 0,
    patterns,
    deathsWithPatterns
  };
}

export function getMatchupAnalytics(games, role = 'ADC') {
  /**
   * Retourne { mainEnemies, allies1, allies2 } selon le rôle :
   *
   * ADC     : mainEnemies = enemyAdc,     allies1 = allySupports (avec synergies),  allies2 = allyJunglers
   * Mid     : mainEnemies = enemyMid,     allies1 = allyJunglers,                   allies2 = enemyJunglers
   * Top     : mainEnemies = enemyTop,     allies1 = allyJunglers,                   allies2 = enemyJunglers
   * Jungle  : mainEnemies = enemyJungler, allies1 = allySupports,                   allies2 = enemySupports
   * Support : mainEnemies = enemySupport, allies1 = allyAdcs,                       allies2 = allyJunglers
   *
   * Structure mainEnemies : [{ name, games, wins, winrate, synergies: [{ name, games, winrate }] }]
   * Structure allies*     : [{ name, games, wins, winrate }]
   *
   * Toutes les listes sont triées par games desc, winrate desc.
   * Synergies = quels alliés clés tu as eu QUAND tu affrontais ce champion ennemi.
   */

  const MAX_VISIBLE = 10;

  // ── helpers ────────────────────────────────────────────────────────────────

  const bucket = () => ({ games: 0, wins: 0, _sub: {} });

  const addTo = (map, key, win, subKey = null) => {
    if (!key) return;
    if (!map[key]) map[key] = bucket();
    map[key].games++;
    if (win) map[key].wins++;
    if (subKey) {
      if (!map[key]._sub[subKey]) map[key]._sub[subKey] = { games: 0, wins: 0 };
      map[key]._sub[subKey].games++;
      if (win) map[key]._sub[subKey].wins++;
    }
  };

  const toArray = (map, withSynergies = false) =>
    Object.entries(map)
      .filter(([k]) => k && k !== 'undefined' && k !== 'null')
      .map(([name, v]) => {
        const entry = {
          name,
          games: v.games,
          wins: v.wins,
          winrate: v.games > 0 ? Math.round((v.wins / v.games) * 100) : 0,
        };
        if (withSynergies) {
          entry.synergies = Object.entries(v._sub || {})
            .filter(([k]) => k && k !== 'undefined')
            .map(([sName, sv]) => ({
              name: sName,
              games: sv.games,
              wins: sv.wins,
              winrate: sv.games > 0 ? Math.round((sv.wins / sv.games) * 100) : 0,
            }))
            .sort((a, b) => b.games - a.games || b.winrate - a.winrate);
        }
        return entry;
      })
      .sort((a, b) => b.games - a.games || b.winrate - a.winrate);

  // ── accumulation selon le rôle ──────────────────────────────────────────────

  const mainMap  = {};
  const ally1Map = {};
  const ally2Map = {};

  const r = role?.toUpperCase() || 'ADC';

  games.forEach(g => {
    const win = g.result === 'W';

    if (r === 'ADC') {
      // mainEnemies = ADC adverse ; synergy = support allié
      addTo(mainMap, g.enemyAdc, win, g.support);
      // allies1 = supports alliés (avec synergy = ADC adverse)
      addTo(ally1Map, g.support, win, g.enemyAdc);
      // allies2 = junglers alliés
      addTo(ally2Map, g.jungler, win);

    } else if (r === 'MID') {
      addTo(mainMap, g.enemyMid, win, g.jungler);
      addTo(ally1Map, g.jungler, win, g.enemyMid);
      addTo(ally2Map, g.enemyJungler, win);

    } else if (r === 'TOP') {
      addTo(mainMap, g.enemyTop, win, g.jungler);
      addTo(ally1Map, g.jungler, win, g.enemyTop);
      addTo(ally2Map, g.enemyJungler, win);

    } else if (r === 'JUNGLE') {
      addTo(mainMap, g.enemyJungler, win, g.support);
      addTo(ally1Map, g.support, win, g.enemyJungler);
      addTo(ally2Map, g.enemySupport, win);

    } else if (r === 'SUPPORT') {
      // mainEnemies = support adverse ; synergy = ADC allié
      addTo(mainMap, g.enemySupport, win, g.allyAdc);
      // allies1 = ADCs alliés
      addTo(ally1Map, g.allyAdc, win, g.enemySupport);
      // allies2 = junglers alliés
      addTo(ally2Map, g.jungler, win);

    } else {
      // Fallback générique ADC
      addTo(mainMap, g.enemyAdc, win, g.support);
      addTo(ally1Map, g.support, win);
      addTo(ally2Map, g.jungler, win);
    }
  });

  // ── labels selon rôle ──────────────────────────────────────────────────────

  const LABELS = {
    ADC:     { main: 'ADC adverse',      ally1: 'Supports alliés',   ally2: 'Junglers alliés' },
    MID:     { main: 'Mid adverse',      ally1: 'Junglers alliés',   ally2: 'Junglers ennemis' },
    TOP:     { main: 'Top adverse',      ally1: 'Junglers alliés',   ally2: 'Junglers ennemis' },
    JUNGLE:  { main: 'Jungler adverse',  ally1: 'Supports alliés',   ally2: 'Supports ennemis' },
    SUPPORT: { main: 'Support adverse',  ally1: 'ADCs alliés',       ally2: 'Junglers alliés' },
  };

  const labels = LABELS[r] || LABELS.ADC;

  return {
    mainEnemies:  toArray(mainMap, true),   // avec synergies
    allies1:      toArray(ally1Map, false),
    allies2:      toArray(ally2Map, false),
    labels,
    maxVisible: MAX_VISIBLE,
  };
}


export function getTrendAnalytics(games) {
  const rollingWindow = 10;
  const trends = [];
  for (let i = 0; i < games.length; i++) {
    const window = games.slice(Math.max(0, i - rollingWindow + 1), i + 1);
    const wins = window.filter(g => g.result === 'W').length;
    const deaths = window.reduce((a, g) => a + g.deaths, 0);
    const cleanGames = window.filter(g => !(g.deathDetails || []).some(d => d.type === 'greed')).length;
    trends.push({
      gameNumber: i + 1,
      winrate: Math.round(wins / window.length * 100),
      avgDeaths: +(deaths / window.length).toFixed(1),
      cleanRatio: Math.round(cleanGames / window.length * 100)
    });
  }

  // Comparaison moitié 1 vs moitié 2
  const half = Math.floor(games.length / 2);
  const firstHalf = games.slice(0, half);
  const secondHalf = games.slice(half);

  const stats = (set) => {
    if (set.length === 0) return { winrate: 0, avgDeaths: 0, cleanRatio: 0 };
    const wins = set.filter(g => g.result === 'W').length;
    const deaths = set.reduce((a, g) => a + g.deaths, 0);
    const clean = set.filter(g => !(g.deathDetails || []).some(d => d.type === 'greed')).length;
    return {
      winrate: Math.round(wins / set.length * 100),
      avgDeaths: +(deaths / set.length).toFixed(1),
      cleanRatio: Math.round(clean / set.length * 100)
    };
  };

  // Streaks (winning / losing)
  let currentStreak = 0;
  let currentStreakType = null;
  let longestWinStreak = 0;
  let longestLoseStreak = 0;
  games.forEach(g => {
    if (currentStreakType === g.result) {
      currentStreak++;
    } else {
      currentStreak = 1;
      currentStreakType = g.result;
    }
    if (g.result === 'W' && currentStreak > longestWinStreak) longestWinStreak = currentStreak;
    if (g.result === 'L' && currentStreak > longestLoseStreak) longestLoseStreak = currentStreak;
  });

  return {
    trends,
    firstHalf: stats(firstHalf),
    secondHalf: stats(secondHalf),
    longestWinStreak,
    longestLoseStreak,
    currentStreak: { type: currentStreakType, count: currentStreak }
  };
}

export function getChampionDeepDive(games, championName) {
  const champGames = games.filter(g => g.champion === championName);
  if (champGames.length === 0) return null;

  const wins = champGames.filter(g => g.result === 'W').length;
  const totalDeaths = champGames.reduce((a, g) => a + g.deaths, 0);
  const totalKills = champGames.reduce((a, g) => a + g.kills, 0);
  const totalAssists = champGames.reduce((a, g) => a + g.assists, 0);

  // Matchups sur ce champion — enrichis lane WR @15 + KDA + dmg
  const matchups = {};
  champGames.forEach(g => {
    if (!matchups[g.enemyAdc]) matchups[g.enemyAdc] = { games: 0, wins: 0, _games: [] };
    matchups[g.enemyAdc].games++;
    if (g.result === 'W') matchups[g.enemyAdc].wins++;
    matchups[g.enemyAdc]._games.push(g);
  });

  // Synergie supports — enrichies lane WR @15 + KDA
  const synergies = {};
  champGames.forEach(g => {
    if (!synergies[g.support]) synergies[g.support] = { games: 0, wins: 0, _games: [] };
    synergies[g.support].games++;
    if (g.result === 'W') synergies[g.support].wins++;
    synergies[g.support]._games.push(g);
  });

  // Damage metrics
  const gamesWithDmg = champGames.filter(g => g.damageDealt > 0 && g.duration > 0);
  const avgDmgPerMin = gamesWithDmg.length > 0 ? Math.round(gamesWithDmg.reduce((a, g) => a + (g.damageDealt / g.duration), 0) / gamesWithDmg.length) : 0;
  const gamesWithShare = champGames.filter(g => g.damageDealt > 0 && g.teamDamage > 0);
  const avgDmgShare = gamesWithShare.length > 0 ? (gamesWithShare.reduce((a, g) => a + (g.damageDealt / g.teamDamage * 100), 0) / gamesWithShare.length).toFixed(1) : null;

  // Progression sur ce champion (winrate rolling 5 games)
  const progression = [];
  for (let i = 0; i < champGames.length; i++) {
    const window = champGames.slice(Math.max(0, i - 4), i + 1);
    const w = window.filter(g => g.result === 'W').length;
    progression.push({
      gameNumber: i + 1,
      winrate: Math.round(w / window.length * 100)
    });
  }

  return {
    name: championName,
    games: champGames.length,
    wins,
    losses: champGames.length - wins,
    winrate: Math.round(wins / champGames.length * 100),
    avgKda: totalDeaths > 0 ? ((totalKills + totalAssists) / totalDeaths).toFixed(2) : 'PERFECT',
    avgDeaths: +(totalDeaths / champGames.length).toFixed(1),
    avgKills: +(totalKills / champGames.length).toFixed(1),
    avgAssists: +(totalAssists / champGames.length).toFixed(1),
    avgDmgPerMin,
    avgDmgShare,
    matchups: Object.entries(matchups).map(([name, v]) => {
      const lane = aggregateLaneStats(v._games, 15);
      const totalK = v._games.reduce((s,g) => s + (g.kills||0), 0);
      const totalD = v._games.reduce((s,g) => s + (g.deaths||0), 0);
      const totalA = v._games.reduce((s,g) => s + (g.assists||0), 0);
      return {
        name,
        games: v.games,
        wins: v.wins,
        winrate: v.games > 0 ? Math.round(v.wins / v.games * 100) : 0,
        laneWr: lane?.laneWinrate ?? null,
        laneTotal: lane?.total ?? 0,
        avgKda: totalD > 0 ? +((totalK + totalA) / totalD).toFixed(2) : null,
      };
    }).sort((a, b) => b.games - a.games),
    synergies: Object.entries(synergies).map(([name, v]) => {
      const lane = aggregateLaneStats(v._games, 15);
      const totalK = v._games.reduce((s,g) => s + (g.kills||0), 0);
      const totalD = v._games.reduce((s,g) => s + (g.deaths||0), 0);
      const totalA = v._games.reduce((s,g) => s + (g.assists||0), 0);
      return {
        name,
        games: v.games,
        wins: v.wins,
        winrate: v.games > 0 ? Math.round(v.wins / v.games * 100) : 0,
        laneWr: lane?.laneWinrate ?? null,
        laneTotal: lane?.total ?? 0,
        avgKda: totalD > 0 ? +((totalK + totalA) / totalD).toFixed(2) : null,
      };
    }).sort((a, b) => b.games - a.games),
    progression
  };
}

export function getSessionAnalytics(sessions) {
  if (sessions.length === 0) return null;

  // Winrate par session
  const timeline = sessions.slice().reverse().map((s, i) => ({
    sessionNumber: i + 1,
    date: new Date(s.closed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
    winrate: s.games_count > 0 ? Math.round(s.wins / s.games_count * 100) : 0,
    games: s.games_count,
    mental: s.mental_global
  }));

  // Corrélation mental ↔ winrate
  const byMental = {};
  sessions.forEach(s => {
    const m = s.mental_global || 'N/A';
    if (!byMental[m]) byMental[m] = { sessions: 0, games: 0, wins: 0 };
    byMental[m].sessions++;
    byMental[m].games += s.games_count;
    byMental[m].wins += s.wins;
  });
  const mentalImpact = Object.entries(byMental).map(([mental, v]) => ({
    mental,
    sessions: v.sessions,
    games: v.games,
    winrate: v.games > 0 ? Math.round(v.wins / v.games * 100) : 0
  })).sort((a, b) => b.winrate - a.winrate);

  // Performance par taille de session
  const bySizeBucket = { '1 game': { s: 0, g: 0, w: 0 }, '2 games': { s: 0, g: 0, w: 0 }, '3 games': { s: 0, g: 0, w: 0 }, '4+ games': { s: 0, g: 0, w: 0 } };
  sessions.forEach(s => {
    let bucket;
    if (s.games_count === 1) bucket = '1 game';
    else if (s.games_count === 2) bucket = '2 games';
    else if (s.games_count === 3) bucket = '3 games';
    else bucket = '4+ games';
    bySizeBucket[bucket].s++;
    bySizeBucket[bucket].g += s.games_count;
    bySizeBucket[bucket].w += s.wins;
  });
  const sizeImpact = Object.entries(bySizeBucket).filter(([_, v]) => v.s > 0).map(([size, v]) => ({
    size,
    sessions: v.s,
    winrate: v.g > 0 ? Math.round(v.w / v.g * 100) : 0
  }));

  return {
    timeline,
    mentalImpact,
    sizeImpact,
    totalSessions: sessions.length,
    totalGames: sessions.reduce((a, s) => a + s.games_count, 0),
    totalWins: sessions.reduce((a, s) => a + (s.wins || 0), 0),
    positiveSessions: sessions.filter(s => s.wins > s.losses).length
  };
}

export function getDamageAnalytics(games) {
  const gamesWithDmg = games.filter(g => g.damageDealt > 0 && g.duration > 0);
  if (gamesWithDmg.length === 0) return null;

  // Progression dmg/min rolling 10 games
  const trends = [];
  for (let i = 0; i < gamesWithDmg.length; i++) {
    const window = gamesWithDmg.slice(Math.max(0, i - 9), i + 1);
    const avgDpm = window.reduce((a, g) => a + (g.damageDealt / g.duration), 0) / window.length;
    const avgShare = window.filter(g => g.teamDamage > 0).length > 0
      ? window.filter(g => g.teamDamage > 0).reduce((a, g) => a + (g.damageDealt / g.teamDamage * 100), 0) / window.filter(g => g.teamDamage > 0).length
      : 0;
    trends.push({
      gameNumber: i + 1,
      dmgPerMin: Math.round(avgDpm),
      dmgShare: +avgShare.toFixed(1)
    });
  }

  // Par champion
  const byChampion = {};
  gamesWithDmg.forEach(g => {
    if (!byChampion[g.champion]) byChampion[g.champion] = { games: 0, totalDpm: 0, totalShare: 0, gamesWithShare: 0 };
    byChampion[g.champion].games++;
    byChampion[g.champion].totalDpm += g.damageDealt / g.duration;
    if (g.teamDamage > 0) {
      byChampion[g.champion].totalShare += g.damageDealt / g.teamDamage * 100;
      byChampion[g.champion].gamesWithShare++;
    }
  });
  const perChampion = Object.entries(byChampion).map(([c, v]) => ({
    champion: c,
    games: v.games,
    avgDpm: Math.round(v.totalDpm / v.games),
    avgShare: v.gamesWithShare > 0 ? +(v.totalShare / v.gamesWithShare).toFixed(1) : null
  })).sort((a, b) => b.avgDpm - a.avgDpm);

  // Corrélation dmg share ↔ winrate
  const bucketsShare = [
    { label: '< 20%', min: 0, max: 20, games: 0, wins: 0 },
    { label: '20-25%', min: 20, max: 25, games: 0, wins: 0 },
    { label: '25-30%', min: 25, max: 30, games: 0, wins: 0 },
    { label: '30-35%', min: 30, max: 35, games: 0, wins: 0 },
    { label: '35%+', min: 35, max: 100, games: 0, wins: 0 }
  ];
  games.filter(g => g.damageDealt > 0 && g.teamDamage > 0).forEach(g => {
    const share = g.damageDealt / g.teamDamage * 100;
    const b = bucketsShare.find(b => share >= b.min && share < b.max);
    if (b) {
      b.games++;
      if (g.result === 'W') b.wins++;
    }
  });
  const shareWinrateCorrel = bucketsShare.filter(b => b.games > 0).map(b => ({
    bucket: b.label,
    games: b.games,
    winrate: Math.round(b.wins / b.games * 100)
  }));

  return {
    trends,
    perChampion,
    shareWinrateCorrel,
    totalGamesWithDmg: gamesWithDmg.length,
    overallAvgDpm: Math.round(gamesWithDmg.reduce((a, g) => a + (g.damageDealt / g.duration), 0) / gamesWithDmg.length)
  };
}

export function getFeelingAnalytics(games) {
  if (games.length === 0) return null;

  // Corrélation feeling ↔ winrate
  const byFeeling = {};
  games.forEach(g => {
    const f = g.feeling || 5;
    if (!byFeeling[f]) byFeeling[f] = { games: 0, wins: 0 };
    byFeeling[f].games++;
    if (g.result === 'W') byFeeling[f].wins++;
  });
  const feelingWinrate = Object.entries(byFeeling).map(([feeling, v]) => ({
    feeling: +feeling,
    games: v.games,
    winrate: v.games > 0 ? Math.round(v.wins / v.games * 100) : 0
  })).sort((a, b) => a.feeling - b.feeling);

  // Corrélation mental ↔ winrate
  const byMental = {};
  games.forEach(g => {
    const m = g.mental || 'N/A';
    if (!byMental[m]) byMental[m] = { games: 0, wins: 0, totalDeaths: 0 };
    byMental[m].games++;
    if (g.result === 'W') byMental[m].wins++;
    byMental[m].totalDeaths += g.deaths;
  });
  const mentalImpact = Object.entries(byMental).map(([mental, v]) => ({
    mental,
    games: v.games,
    winrate: v.games > 0 ? Math.round(v.wins / v.games * 100) : 0,
    avgDeaths: v.games > 0 ? +(v.totalDeaths / v.games).toFixed(1) : 0
  })).sort((a, b) => b.winrate - a.winrate);

  return { feelingWinrate, mentalImpact };
}

// ============================================================
// V6.4 — Stats Pre-Queue + Match intent ↔ résultat
// ============================================================

/**
 * Évalue le succès d'un intent en le comparant au résultat de la game matchée
 * Returns : 'success' | 'partial' | 'fail' | null
 *
 * Critères :
 * - intended_pick respecté ? (champion identique)
 * - mental >= 6 et game gagnée + ≤ 4 morts → success
 * - mental >= 6 et résultat moyen → partial
 * - mental < 6 ou défaite + ≥ 5 morts → fail
 */
export function evaluateIntentMatch(intent, game) {
  if (!intent || !game) return null;

  let pickMatched = true;
  if (intent.intended_pick && game.champion) {
    pickMatched = intent.intended_pick.toLowerCase() === game.champion.toLowerCase();
  }

  const isWin = game.result === 'W';
  const deaths = game.deaths || 0;
  const mentalGood = (intent.mental_pre_queue || 5) >= 6;

  // Logique d'évaluation
  let score = 0;
  if (pickMatched) score += 1;
  if (isWin) score += 2;
  else score -= 1;
  if (deaths <= 3) score += 1;
  else if (deaths >= 6) score -= 1;
  if (mentalGood) score += 0.5;

  if (score >= 3) return 'success';
  if (score >= 1) return 'partial';
  return 'fail';
}

/**
 * Calcule les stats globales Pre-Queue
 * Compare les games avec intent vs sans intent
 */
export function getPreQueueAnalytics(games, intents) {
  if (!games || games.length === 0) {
    return { hasData: false };
  }

  // Map des intents par matched_game_id
  const intentsByGameId = {};
  (intents || []).forEach(i => {
    if (i.matched_game_id) {
      intentsByGameId[i.matched_game_id] = i;
    }
  });

  // Séparer games avec et sans intent
  const gamesWithIntent = games.filter(g => intentsByGameId[g.id]);
  const gamesWithoutIntent = games.filter(g => !intentsByGameId[g.id]);

  const lockedRatio = games.length > 0 ? Math.round(gamesWithIntent.length / games.length * 100) : 0;

  // Calc stats avec intent
  const wrWithIntent = gamesWithIntent.length > 0
    ? Math.round(gamesWithIntent.filter(g => g.result === 'W').length / gamesWithIntent.length * 100)
    : 0;
  const avgDeathsWithIntent = gamesWithIntent.length > 0
    ? +(gamesWithIntent.reduce((s, g) => s + (g.deaths || 0), 0) / gamesWithIntent.length).toFixed(1)
    : 0;

  // Calc stats sans intent
  const wrWithoutIntent = gamesWithoutIntent.length > 0
    ? Math.round(gamesWithoutIntent.filter(g => g.result === 'W').length / gamesWithoutIntent.length * 100)
    : 0;
  const avgDeathsWithoutIntent = gamesWithoutIntent.length > 0
    ? +(gamesWithoutIntent.reduce((s, g) => s + (g.deaths || 0), 0) / gamesWithoutIntent.length).toFixed(1)
    : 0;

  // Evaluation des intents matchés
  const matchedIntents = (intents || []).filter(i => i.matched_game_id);
  const evaluations = matchedIntents.map(intent => {
    const game = games.find(g => g.id === intent.matched_game_id);
    return {
      intent,
      game,
      result: evaluateIntentMatch(intent, game)
    };
  }).filter(e => e.result !== null);

  const successCount = evaluations.filter(e => e.result === 'success').length;
  const partialCount = evaluations.filter(e => e.result === 'partial').length;
  const failCount = evaluations.filter(e => e.result === 'fail').length;
  const successRate = evaluations.length > 0 ? Math.round(successCount / evaluations.length * 100) : 0;

  // Différentiel WR (impact du pre-queue)
  const wrDelta = wrWithIntent - wrWithoutIntent;
  const deathsDelta = avgDeathsWithIntent - avgDeathsWithoutIntent;

  // Recent matched intents (5 derniers pour historique)
  const recentEvaluations = evaluations.slice(-5).reverse();

  return {
    hasData: true,
    totalGames: games.length,
    totalIntents: intents?.length || 0,
    matchedIntents: matchedIntents.length,
    lockedRatio,
    gamesWithIntent: gamesWithIntent.length,
    gamesWithoutIntent: gamesWithoutIntent.length,
    wrWithIntent,
    wrWithoutIntent,
    wrDelta,
    avgDeathsWithIntent,
    avgDeathsWithoutIntent,
    deathsDelta,
    successCount,
    partialCount,
    failCount,
    successRate,
    recentEvaluations
  };
}

// ============================================================
// V6.7 — Calcul du LP delta par game
// ============================================================

/**
 * Pour chaque game, retourne le LP delta calculé via les rank_snapshots.
 * Un snapshot avec game_id = X correspond au rang APRÈS la game X.
 * Le delta = score(snapshot[X]) - score(snapshot précédent chronologique).
 *
 * @param {Array} games - liste des games triées chronologiquement
 * @param {Array} snapshots - liste des rank_snapshots
 * @returns {Map<gameId, { delta, lpAfter, snapshot }>}
 */
export function computeLpDeltasByGame(games, snapshots) {
  const result = new Map();
  if (!snapshots || snapshots.length === 0) return result;

  // Helper : convertit un snapshot en score numérique (cohérent avec RankSection)
  const scoreOf = (s) => {
    const tierBase = {
      IRON: 0, BRONZE: 400, SILVER: 800, GOLD: 1200, PLATINUM: 1600,
      EMERALD: 2000, DIAMOND: 2400, MASTER: 2800, GRANDMASTER: 2800, CHALLENGER: 2800
    };
    const rankBase = { IV: 0, III: 100, II: 200, I: 300 };
    const base = tierBase[s.tier] || 0;
    const rankAdd = ['MASTER', 'GRANDMASTER', 'CHALLENGER'].includes(s.tier) ? 0 : (rankBase[s.rank] || 0);
    return base + rankAdd + (s.lp || 0);
  };

  // Trier les snapshots chronologiquement
  const sortedSnaps = [...snapshots].sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));

  // Pour chaque snapshot lié à une game, calculer le delta vs snapshot précédent
  for (let i = 0; i < sortedSnaps.length; i++) {
    const snap = sortedSnaps[i];
    if (!snap.game_id) continue; // snapshot manuel sans game associée

    const prev = i > 0 ? sortedSnaps[i - 1] : null;
    const delta = prev ? scoreOf(snap) - scoreOf(prev) : null;

    result.set(snap.game_id, {
      delta,
      lpAfter: snap.lp,
      tier: snap.tier,
      rank: snap.rank,
      snapshot: snap
    });
  }

  return result;
}

// ============================================================
// V6.7 — Détection des triggers pour suggestions intelligentes
// ============================================================

/**
 * Analyse les games récentes pour détecter des triggers actionnables.
 * Retourne 0 ou plusieurs triggers avec une suggestion concrète chacun.
 *
 * Triggers v6.7 (3 simples) :
 * - LOSS_STREAK : 3+ défaites consécutives récentes
 * - CHAMPION_LOSS_PATTERN : 60%+ de défaites sur un champion sur les 5 dernières games avec lui
 * - HEAVY_DEATH_GAME : game à 8+ morts dans les 3 dernières games
 *
 * @param {Array} games - liste des games triées chronologiquement
 * @param {Array} existingBlocks - blocs déjà actifs (pour éviter doublons)
 * @returns {Array} triggers détectés avec suggestion associée
 */
export function detectImportTriggers(games, existingBlocks = [], championPool = []) {
  const triggers = [];
  if (!games || games.length < 3) return triggers;

  // V6.8 : champions mis de côté à ignorer dans les suggestions
  const sidelinedSet = new Set(
    (championPool || [])
      .filter(p => p.status === 'sidelined')
      .map(p => p.name.toLowerCase())
  );

  const recent = games.slice(-10); // les 10 dernières games
  const last5 = games.slice(-5);
  const last3 = games.slice(-3);

  const activeBlocks = existingBlocks.filter(b => b.status === 'active');
  const hasActiveOnMetric = (metric) => activeBlocks.some(b => b.metric_type === metric);

  // === Trigger 1 : LOSS_STREAK ===
  // 3+ défaites consécutives en fin d'historique
  let streak = 0;
  for (let i = games.length - 1; i >= 0; i--) {
    if (games[i].result === 'L') streak++;
    else break;
  }
  if (streak >= 3) {
    triggers.push({
      id: 'loss_streak',
      severity: 'high',
      title: `${streak} défaites consécutives détectées`,
      detail: `Tu enchaînes ${streak} losses. C'est le moment idéal pour faire une pause ou repartir sur des bases saines.`,
      suggestion: {
        title: 'Reset mental — 5 games clean',
        description: `Objectif : 5 games avec ≤ 4 morts pour casser la streak. Pas de pression sur le winrate, focus discipline.`,
        metric_type: 'avg_deaths',
        metric_operator: '<=',
        metric_target: 4,
        target_games: 5
      }
    });
  }

  // === Trigger 2 : CHAMPION_LOSS_PATTERN ===
  // Sur les 10 dernières games, un champion avec 3+ games dont 60%+ de losses
  const byChamp = {};
  recent.forEach(g => {
    if (!byChamp[g.champion]) byChamp[g.champion] = { games: 0, losses: 0 };
    byChamp[g.champion].games++;
    if (g.result === 'L') byChamp[g.champion].losses++;
  });

  let worstChamp = null;
  Object.entries(byChamp).forEach(([champ, stats]) => {
    // V6.8 : ignorer les champions mis de côté
    if (sidelinedSet.has(champ.toLowerCase())) return;
    if (stats.games >= 3) {
      const lossRate = stats.losses / stats.games;
      if (lossRate >= 0.6) {
        if (!worstChamp || (stats.losses / stats.games) > (worstChamp.lossRate)) {
          worstChamp = { champion: champ, ...stats, lossRate };
        }
      }
    }
  });

  if (worstChamp && !hasActiveOnMetric('winrate')) {
    triggers.push({
      id: 'champion_loss_pattern',
      severity: 'medium',
      title: `${worstChamp.champion} : ${worstChamp.losses}L/${worstChamp.games}G récemment`,
      detail: `Tu performes mal sur ${worstChamp.champion} (${Math.round(worstChamp.lossRate * 100)}% de losses sur 10 games). Soit tu travailles le matchup, soit tu mets de côté.`,
      suggestion: {
        title: `Remontée WR ${worstChamp.champion}`,
        description: `Objectif : 50%+ winrate sur 8 games avec ${worstChamp.champion}, ou décider de bench le pick.`,
        metric_type: 'winrate',
        metric_operator: '>=',
        metric_target: 50,
        target_games: 8,
        filter_champion: worstChamp.champion
      }
    });
  }

  // === Trigger 3 : HEAVY_DEATH_GAME ===
  // Au moins une game à 8+ morts dans les 3 dernières
  const heavyDeath = last3.find(g => (g.deaths || 0) >= 8);
  if (heavyDeath && !hasActiveOnMetric('avg_deaths') && !triggers.some(t => t.id === 'loss_streak')) {
    triggers.push({
      id: 'heavy_death_game',
      severity: 'medium',
      title: `Game à ${heavyDeath.deaths} morts`,
      detail: `Une de tes 3 dernières games s'est terminée à ${heavyDeath.deaths} morts (${heavyDeath.champion} vs ${heavyDeath.enemyAdc}). Inflater = signal de tilt ou matchup mal géré.`,
      suggestion: {
        title: 'Bloc discipline — limiter les inflate',
        description: `Objectif : ≤ 5 morts max par game sur 10 games. Si tu atteins 6+ morts, tu reconnais qu'il faut revenir à du safe play.`,
        metric_type: 'avg_deaths',
        metric_operator: '<=',
        metric_target: 5,
        target_games: 10
      }
    });
  }

  return triggers;
}

// ============================================================
// V6.9 — Lane Score à 15 min (formule pondérée)
// ============================================================

/**
 * Calcule le score de lane pour une game donnée à un minute précise.
 * Formule pondérée : Gold (1 pt / 200g) + XP (1 pt / 200 XP) + CS (1 pt / 5 CS)
 *
 * @param {Object} game - la game avec timeFrames
 * @param {number} minute - la minute cible (default 15)
 * @returns {Object|null} { score, gold, xp, cs, status } ou null si pas de data
 *   status : 'won' (≥+3) | 'drawn' (entre 0 et +3) | 'lost' (<0)
 */
/**
 * isLaneWon — score pondéré par rôle (v8.21.2)
 *
 * Pondération définie avec Alexis :
 *   ADC     : CS 35% | Gold 30% | XP 15% | Kills directs 20%
 *   Mid     : CS 25% | Gold 25% | XP 25% | Kills 15% | Roaming kills 10%
 *   Top     : CS 30% | Gold 20% | XP 30% | Kills 20%
 *   Jungle  : Gold 35% | XP 25% | Kills 20% | Obj dmg 20% (CS ignoré)
 *   Support : retourne null — KP à la place
 *
 * Unités de normalisation :
 *   Gold  : /300  (écart de 300 gold ≈ 1 pt)
 *   XP    : /400  (XP scale plus fort que Gold, seuil plus haut)
 *   CS    : /5    (écart de 5 cs ≈ 1 pt)
 *   Kills : *1.5  (chaque kill direct = 1.5 pt)
 *
 * Seuil de victoire : score > 0 (tu es devant sur l'ensemble pondéré)
 * Support → retourne null.
 */
const LANE_WEIGHTS = {
  ADC:    { gold: 0.30, xp: 0.15, cs: 0.35, kills: 0.20 },
  MID:    { gold: 0.25, xp: 0.25, cs: 0.25, kills: 0.15, roamKills: 0.10 },
  TOP:    { gold: 0.20, xp: 0.30, cs: 0.30, kills: 0.20 },
  JUNGLE: { gold: 0.35, xp: 0.25, cs: 0.00, kills: 0.20, objDmg: 0.20 },
  SUPPORT: null,
};

export function isLaneWon(game, minute = 15, role = 'ADC') {
  const r = (role || 'ADC').toUpperCase();
  const w = LANE_WEIGHTS[r];
  if (w === null || w === undefined) return null; // Support ou rôle inconnu

  const tf = game?.timeFrames?.[`min${minute}`];
  if (!tf || !tf.my || !tf.opp) return null;

  const goldDiff = (tf.my.gold || 0) - (tf.opp.gold || 0);
  const csDiff   = (tf.my.cs   || 0) - (tf.opp.cs   || 0);
  const xpDiff   = (tf.my.xp   || 0) - (tf.opp.xp   || 0);
  const directKills = game?.opponentStats?.killsOfMe ?? 0;

  const goldPts  = (goldDiff / 300) * w.gold;
  const xpPts    = (xpDiff   / 150) * w.xp;   // /150 : +500 XP seul = won
  const csPts    = (csDiff   / 8  ) * w.cs;   // /8   : +12 CS seul = won
  const killPts  = directKills * 1.5 * (w.kills || 0);

  const score = goldPts + xpPts + csPts + killPts;
  if (score > 0.5)  return 'won';
  if (score < -0.5) return 'lost';
  return 'drawn';
}

export function computeLaneScore(game, minute = 15, role = 'ADC') {
  const r = (role || 'ADC').toUpperCase();
  const w = LANE_WEIGHTS[r];
  if (w === null || w === undefined) return null; // Support ou rôle inconnu

  const tf = game?.timeFrames?.[`min${minute}`];
  if (!tf || !tf.my || !tf.opp) return null;
  const goldDiff = (tf.my.gold || 0) - (tf.opp.gold || 0);
  const csDiff   = (tf.my.cs   || 0) - (tf.opp.cs   || 0);
  const xpDiff   = (tf.my.xp   || 0) - (tf.opp.xp   || 0);
  const directKills = game?.opponentStats?.killsOfMe ?? 0;

  const goldPts  = (goldDiff / 300) * w.gold;
  const xpPts    = (xpDiff   / 150) * w.xp;   // /150 : +500 XP seul = won
  const csPts    = (csDiff   / 8  ) * w.cs;   // /8   : +12 CS seul = won
  const killPts  = directKills * 1.5 * (w.kills || 0);
  const score    = +(goldPts + xpPts + csPts + killPts).toFixed(3);

  const status = score > 0.5 ? 'won' : score < -0.5 ? 'lost' : 'drawn';
  return {
    goldDiff,
    xpDiff,
    csDiff,
    directKills,
    score,
    status,
    role: r,
  };
}

/**
 * Agrège les won/drawn/lost lanes sur un ensemble de games.
 */
export function aggregateLaneStats(games, minute = 15, role = 'ADC') {
  const scores = games
    .map(g => ({ game: g, score: computeLaneScore(g, minute, role) }))
    .filter(x => x.score !== null);

  const total = scores.length;
  if (total === 0) return null;

  const won   = scores.filter(x => x.score.status === 'won').length;
  const drawn = scores.filter(x => x.score.status === 'drawn').length;
  const lost  = scores.filter(x => x.score.status === 'lost').length;

  // Drawn compte pour 0.5 dans le winrate (seuils +0.5/-0.5)
  const laneWinrate = total > 0 ? Math.round(((won + drawn * 0.5) / total) * 100) : 0;
  // Lane score moyen
  const avgScore = total > 0
    ? +(scores.reduce((s, x) => s + x.score.score, 0) / total).toFixed(2)
    : 0;

  return {
    total,
    won,
    drawn,
    lost,
    laneWinrate,
    avgScore,
    pctWon: Math.round(won / total * 100),
    pctDrawn: Math.round(drawn / total * 100),
    pctLost: Math.round(lost / total * 100)
  };
}

// ============================================================
// V6.9 — Morts importantes (game-losing)
// ============================================================

/**
 * Filtre les morts dont la net value est < -800 gold.
 * Retourne les death events avec leur game associée.
 */
export function getCriticalDeaths(games, championFilter = null) {
  const critical = [];
  (games || []).forEach(g => {
    if (championFilter && g.champion !== championFilter) return;
    (g.deathDetails || []).forEach((d, idx) => {
      const netValue = d.netValue || d.gold_lost || 0;
      if (netValue <= -800) {
        critical.push({
          ...d,
          gameId: g.id,
          gameNumber: g.gameNumber,
          champion: g.champion,
          enemyAdc: g.enemyAdc,
          result: g.result,
          deathIndex: idx,
          netValue
        });
      }
    });
  });
  // Tri par net value (plus coûteuse d'abord)
  critical.sort((a, b) => a.netValue - b.netValue);
  return critical;
}

// ============================================================
// CHAMPION STATS (déplacé depuis DashboardBottomPanel v8.6)
// ============================================================

/**
 * Calcule les stats WR/games par champion sur une fenêtre glissante.
 */
export function computeChampionStats(games, withinDays = 30) {
  if (!Array.isArray(games) || games.length === 0) return [];
  const cutoff = Date.now() - withinDays * DAY_MS;
  const recent = games.filter(g => new Date(g.timestamp) > cutoff);
  const byChamp = {};
  recent.forEach(g => {
    if (!g.champion) return;
    if (!byChamp[g.champion]) byChamp[g.champion] = { name: g.champion, total: 0, wins: 0 };
    byChamp[g.champion].total++;
    if (g.result === 'W') byChamp[g.champion].wins++;
  });
  return Object.values(byChamp)
    .map(c => ({ ...c, wr: Math.round((c.wins / c.total) * 100) }))
    .sort((a, b) => b.total - a.total);
}

// ============================================================
// INSIGHTS DÉTECTION (déplacé depuis DashboardBottomPanel v8.6)
// ============================================================



export function detectStrength(games) {
  if (!Array.isArray(games) || games.length === 0) return null;
  const lane = aggregateLaneStats(games, 15);
  if (lane && lane.total >= 5 && lane.laneWinrate >= 65) {
    return { type: 'strength', eyebrow: '✨ POINT FORT', title: 'Lane phase solide',
      detail: `${lane.laneWinrate}% Lane WR sur ${lane.total} games.`, barColor: 'cyan' };
  }
  const withCs = games.filter(g => g.cs_per_min && g.cs_per_min > 0);
  if (withCs.length >= 5) {
    const avgCs = withCs.reduce((s, g) => s + g.cs_per_min, 0) / withCs.length;
    if (avgCs >= 8) return { type: 'strength', eyebrow: '✨ POINT FORT', title: 'Wave management propre',
      detail: `${avgCs.toFixed(1)} CS/min en moyenne.`, barColor: 'cyan' };
  }
  if (games.length >= 5) {
    const avgDeaths = games.reduce((s, g) => s + (g.deaths || 0), 0) / games.length;
    if (avgDeaths <= 3.5) return { type: 'strength', eyebrow: '✨ POINT FORT', title: 'Discipline morts',
      detail: `${avgDeaths.toFixed(1)} morts/game en moyenne.`, barColor: 'cyan' };
  }
  return null;
}

export function detectWeakness(games) {
  if (!Array.isArray(games) || games.length === 0) return null;
  const deathsData = getDeathsAnalytics(games);
  if (deathsData?.byType) {
    const greedCount = deathsData.byType.greed || 0;
    const totalDeaths = (deathsData.byType.greed || 0) + (deathsData.byType.position || 0) + (deathsData.byType.investment || 0);
    if (totalDeaths >= 10 && greedCount / totalDeaths >= 0.4)
      return { type: 'weakness', eyebrow: '⚠ À TRAVAILLER', title: 'Greed deaths trop fréquentes',
        detail: `${greedCount}/${totalDeaths} morts greed (${Math.round(greedCount/totalDeaths*100)}%).`, barColor: 'amber' };
  }
  const lane = aggregateLaneStats(games, 15);
  if (lane && lane.total >= 5 && lane.laneWinrate < 35)
    return { type: 'weakness', eyebrow: '⚠ À TRAVAILLER', title: 'Lane phase difficile',
      detail: `${lane.laneWinrate}% Lane WR sur ${lane.total} games.`, barColor: 'amber' };
  if (games.length >= 5) {
    const avgDeaths = games.reduce((s, g) => s + (g.deaths || 0), 0) / games.length;
    if (avgDeaths >= 6) return { type: 'weakness', eyebrow: '⚠ À TRAVAILLER', title: 'Trop de morts par game',
      detail: `${avgDeaths.toFixed(1)} morts/game en moyenne.`, barColor: 'amber' };
  }
  return null;
}

export function detectSuggestion(games, userSettings) {
  if (!Array.isArray(games) || games.length === 0) return null;
  const cutoff = Date.now() - WEEK_MS;
  const recent = games.filter(g => new Date(g.timestamp) > cutoff);
  const byChamp = {};
  recent.forEach(g => {
    if (!g.champion) return;
    if (!byChamp[g.champion]) byChamp[g.champion] = { wins: 0, total: 0 };
    byChamp[g.champion].total++;
    if (g.result === 'W') byChamp[g.champion].wins++;
  });
  const poolRaw = userSettings?.champion_pool;
  const poolArray = Array.isArray(poolRaw) ? poolRaw : [];
  const sidelinedSet = new Set(poolArray.filter(c => c?.status === 'sidelined').map(c => c.name));
  const worstChamp = Object.entries(byChamp)
    .filter(([name, s]) => s.total >= 3 && !sidelinedSet.has(name))
    .map(([name, s]) => ({ name, wr: Math.round((s.wins / s.total) * 100), total: s.total }))
    .sort((a, b) => a.wr - b.wr)[0];
  if (worstChamp && worstChamp.wr <= 33)
    return { type: 'suggestion', eyebrow: '💡 SUGGESTION',
      title: `Sidelined ${worstChamp.name} ?`,
      detail: `${worstChamp.wr}% WR sur ${worstChamp.total} dernières games.`,
      barColor: 'violet', action: { type: 'sideline_champion', champion: worstChamp.name } };
  if (recent.length === 0 && games.length > 0)
    return { type: 'suggestion', eyebrow: '💡 SUGGESTION', title: 'Importe tes dernières games',
      detail: "Ça fait plus de 7 jours sans activité.", barColor: 'violet', action: { type: 'open_import' } };
  return null;
}
